import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Lock, LogIn } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border);
  border-top: 3px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: var(--color-text-muted);
  font-size: 1rem;
`;

const UnauthorizedContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: var(--spacing-2xl);
`;

const UnauthorizedCard = styled.div`
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-2xl);
  text-align: center;
  max-width: 400px;
  width: 100%;
  box-shadow: var(--shadow-lg);
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-lg);
  
  .icon {
    width: 64px;
    height: 64px;
    color: var(--color-primary);
    background: rgba(102, 126, 234, 0.1);
    border-radius: var(--border-radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Title = styled.h2`
  color: var(--color-text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: var(--spacing-md);
`;

const Description = styled.p`
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin-bottom: var(--spacing-xl);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: var(--spacing-md);
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 1rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all var(--animation-duration) ease;
  min-height: 44px;
  
  &.primary {
    background: var(--gradient-primary);
    color: white;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(102, 126, 234, 0.6);
    }
  }
  
  &.secondary {
    background: var(--color-background);
    color: var(--color-primary);
    border: 2px solid var(--color-primary);
    
    &:hover {
      background: var(--color-primary);
      color: white;
      transform: translateY(-2px);
    }
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const ProtectedRoute = ({ children, requireAuth = true, fallback = null }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // 如果正在加载用户信息，显示加载状态
  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>正在验证身份...</LoadingText>
      </LoadingContainer>
    );
  }

  // 如果需要认证但用户未登录
  if (requireAuth && !isAuthenticated) {
    // 如果提供了自定义的fallback组件，使用它
    if (fallback) {
      return fallback;
    }

    // 显示未授权页面
    return (
      <UnauthorizedContainer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <UnauthorizedCard>
          <IconContainer>
            <div className="icon">
              <Lock size={32} />
            </div>
          </IconContainer>
          
          <Title>需要登录</Title>
          
          <Description>
            您需要登录才能访问此页面。请登录您的账户或创建新账户来继续。
          </Description>
          
          <ActionButtons>
            <Button 
              className="primary"
              onClick={() => {
                // 保存当前路径，登录后重定向回来
                const returnUrl = location.pathname + location.search;
                window.location.href = `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
              }}
            >
              <LogIn />
              立即登录
            </Button>
            
            <Button 
              className="secondary"
              onClick={() => {
                const returnUrl = location.pathname + location.search;
                window.location.href = `/register?returnUrl=${encodeURIComponent(returnUrl)}`;
              }}
            >
              注册账户
            </Button>
          </ActionButtons>
        </UnauthorizedCard>
      </UnauthorizedContainer>
    );
  }

  // 如果不需要认证，或者用户已登录，渲染子组件
  return children;
};

// 高阶组件版本
export const withAuth = (Component, options = {}) => {
  return function AuthenticatedComponent(props) {
    return (
      <ProtectedRoute {...options}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
};

// 管理员权限保护
export const AdminRoute = ({ children, fallback = null }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>正在验证权限...</LoadingText>
      </LoadingContainer>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.role !== 'admin') {
    if (fallback) {
      return fallback;
    }

    return (
      <UnauthorizedContainer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <UnauthorizedCard>
          <IconContainer>
            <div className="icon">
              <Lock size={32} />
            </div>
          </IconContainer>
          
          <Title>权限不足</Title>
          
          <Description>
            您没有访问此页面的权限。此页面仅限管理员访问。
          </Description>
          
          <ActionButtons>
            <Button 
              className="primary"
              onClick={() => window.history.back()}
            >
              返回上页
            </Button>
            
            <Button 
              className="secondary"
              onClick={() => window.location.href = '/'}
            >
              回到首页
            </Button>
          </ActionButtons>
        </UnauthorizedCard>
      </UnauthorizedContainer>
    );
  }

  return children;
};

// 可选认证路由（用户可能登录也可能未登录）
export const OptionalAuthRoute = ({ children }) => {
  const { loading } = useAuth();

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>正在加载...</LoadingText>
      </LoadingContainer>
    );
  }

  return children;
};

export default ProtectedRoute;
